import { useMemo } from "react";

import { useCurrentUser } from "@/features/auth/model/useAuth";
import { useGetMyPermissionsQuery } from "@/features/manifest/api/permissionsApi";
import {
  type PermissionCapability,
  capabilityField,
} from "@/features/manifest/model/permissionModuleMap";

/**
 * Modules where "whose records" is a real question. Mirrors
 * manifest.models.SCOPED_MODULES on the backend.
 */
const SCOPED_MODULES = new Set(["scheduling"]);

/** Roles that read a scoped module org-wide. Mirrors SCOPE_ALL_ROLES. */
const SCOPE_ALL_ROLES = new Set([
  "OWNER",
  "ADMIN",
  "PROGRAM_DIRECTOR",
  "ACCOUNTING_ASSISTANT",
  "ACCOUNTANT_OF_RECORD",
]);

/**
 * The role default, mirroring manifest.default_scope_for.
 *
 * This is the fallback for a backend that predates the scope field: an
 * explicit scope from the server always wins, but without one a staff
 * member would otherwise be handed the whole org's roster. Getting the
 * default wrong in that direction leaks; getting it wrong the other way
 * only hides rows from a manager, who can be re-scoped in the panel.
 */
function defaultScopeFor(role: string | undefined, module: string): "own" | "all" {
  if (!SCOPED_MODULES.has(module)) return "all";
  if (!role) return "all";
  return SCOPE_ALL_ROLES.has(role) ? "all" : "own";
}

export function useModulePermissions() {
  const { data, isLoading, isFetching } = useGetMyPermissionsQuery();
  const user = useCurrentUser();

  const fullAccess = useMemo(() => {
    if (!data) return false;
    return "full_access" in data && data.full_access === true;
  }, [data]);

  const hasCapability = useMemo(() => {
    return (module: string, capability: PermissionCapability = "view"): boolean => {
      if (fullAccess) return true;
      if (!data || "full_access" in data) return false;
      const row = data[module];
      if (!row) return false;
      const key = capabilityField(capability) as keyof typeof row;
      return Boolean(row[key]);
    };
  }, [data, fullAccess]);

  /**
   * Whose records this user reads on a module.
   *
   * An explicit scope from the server wins. Where the field is absent -
   * an unscoped module, or a backend deployed before the migration - the
   * role default fills in, so the schedule behaves the same whether or
   * not the backend has shipped yet. Full-access roles are org-wide.
   */
  const moduleScope = useMemo(() => {
    return (module: string): "own" | "all" => {
      if (fullAccess) return "all";
      if (!data || "full_access" in data) {
        // Secure default while permissions load or when the payload is missing.
        return defaultScopeFor(user?.role, module);
      }
      return data[module]?.scope ?? defaultScopeFor(user?.role, module);
    };
  }, [data, fullAccess, user?.role]);

  return {
    loading: isLoading || isFetching,
    fullAccess,
    canView: (module: string) => hasCapability(module, "view"),
    hasCapability,
    moduleScope,
    /** True when the user reads only their own rows on `module`. */
    isScopedToOwn: (module: string) => moduleScope(module) === "own",
  };
}

export function useModulePermission(
  module: string,
  capability: PermissionCapability = "view",
) {
  const { loading, fullAccess, hasCapability } = useModulePermissions();
  return {
    loading,
    fullAccess,
    allowed: hasCapability(module, capability),
  };
}
